import React from 'react';

import cs from 'classnames';
import styled from 'styled-components';

import {LazyTooltip} from 'app/components/general/LazyTooltip';
import {ViewModeTitle} from 'app/general/detailpage/ViewModeTitle';
import {
  BootstrapMinLargeMediaQuery,
  BootstrapMinMediumMediaQuery,
} from 'app/general/mediaqueries';
import {gt} from 'app/utils/i18n';

import {LoadableBaseDiagram} from './BaseDiagram';
import {makeDiagramEditable} from './make-editable';
import {PrintDiagramButton} from './PrintDiagramButton';

import type {DocumentVersionContentType} from 'app/acceptance/types';
import type {ApplyDocumentShaperFuncType} from 'app/steps/document-shaper';
import type {AnyStepType} from 'sweet-types';

const StyledContainerDiv = styled.div`
  > div > .btn-toolbar {
    position: absolute;
    left: 10px;
    top: 10px;
  }

  .btn-toolbar,
  .title-buttons {
    .btn {
      font-size: 70%;
    }
  }
`;
export const diagramPaperOptions = {
  defaultRouter: {
    name: 'manhattan',
    args: {
      padding: 10,
      startDirections: ['bottom'],
      endDirections: ['top'],
    },
  },
  background: {color: '#FFF'},
};
export const diagramDesktopLayoutOptions = {
  portrait: {
    nodeSep: 70,
    rankSep: 50,
  },
  landscape: {
    nodeSep: 50,
    rankSep: 70,
  },
};

export const diagramFullscreenPaperOptions = {
  defaultRouter: {
    name: 'manhattan',
    args: {
      padding: 10,
      startDirections: ['right', 'bottom'],
      endDirections: ['left', 'top'],
    },
  },
  background: {color: '#FFF'},
};

export const diagramFullscreenLayoutOptions = {
  portrait: {
    nodeSep: 50,
    rankSep: 60,
  },
  landscape: {
    nodeSep: 50,
    rankSep: 60,
  },
};

export const diagramGeneralOptions: {
  positionContent: string;
  forceOrientation: 'portrait';
} = {
  positionContent: 'top',
  forceOrientation: 'portrait',
};

type Props = {
  onStepClick: (id: string) => void;
  onApplyDocumentShaper?: ApplyDocumentShaperFuncType;
  versionId: string | number;
  versionContentType: DocumentVersionContentType;
  steps: Array<AnyStepType>;
  toggleSidebarDiagram: () => void;
  toggleOverlayDiagram: () => void;
  elements?: 'view' | 'edit';
  onCreateStep?: (name: string) => void;
  createStepLabel?: string;
};

const BaseProcedureDiagramSidebar = ({
  versionId,
  versionContentType,
  steps,
  toggleSidebarDiagram,
  toggleOverlayDiagram,
  onStepClick,
  elements = 'view',
  onApplyDocumentShaper,
  onCreateStep,
  createStepLabel,
}: Props) => (
  <LoadableBaseDiagram
    elements={elements}
    onApplyDocumentShaper={onApplyDocumentShaper}
    onStepClick={onStepClick}
    toggleDiagram={toggleSidebarDiagram}
    layoutOptions={diagramDesktopLayoutOptions}
    paperOptions={diagramPaperOptions}
    generalOptions={diagramGeneralOptions}
    steps={steps}
    onCreateStep={onCreateStep}
    createStepLabel={createStepLabel}
  >
    {(diagram, zoomControl) => (
      <StyledContainerDiv className="d-flex flex-column h-100">
        <ViewModeTitle
          title={gt('Overview')}
          actionsBox={
            <div className="btn-group title-buttons d-print-none">
              <LazyTooltip tooltip={gt('Print diagram to image')}>
                <PrintDiagramButton
                  versionId={versionId}
                  versionContentType={versionContentType}
                />
              </LazyTooltip>
              <LazyTooltip tooltip={gt('Expand overview')}>
                <button
                  className="btn btn-sm btn-outline-secondary"
                  onClick={toggleOverlayDiagram}
                >
                  <i className="fas fa-fw fa-expand-arrows-alt" />
                </button>
              </LazyTooltip>
              <LazyTooltip tooltip={gt('Hide overview')}>
                <button
                  className="btn btn-sm btn-outline-secondary"
                  onClick={toggleSidebarDiagram}
                >
                  <i className="fas fa-fw fa-chevron-right" />
                </button>
              </LazyTooltip>
            </div>
          }
        />
        <div className="flex-grow-1 position-relative">
          {zoomControl}
          {diagram}
        </div>
      </StyledContainerDiv>
    )}
  </LoadableBaseDiagram>
);

type ProcedureDiagramSidebarProps = Props & {className?: string};

export const ProcedureDiagramSidebar = ({
  className,
  ...props
}: ProcedureDiagramSidebarProps) => (
  <div className={cs('sticky-top d-print-none bg-white vh-100', className)}>
    <BootstrapMinMediumMediaQuery>
      <BaseProcedureDiagramSidebar {...props} />
    </BootstrapMinMediumMediaQuery>
  </div>
);

export const DetailPageProcedureDiagramSidebar = ({
  className,
  ...props
}: ProcedureDiagramSidebarProps) => (
  <div className={cs('sticky-top d-print-none bg-white vh-100', className)}>
    <BootstrapMinLargeMediaQuery>
      <BaseProcedureDiagramSidebar {...props} />
    </BootstrapMinLargeMediaQuery>
  </div>
);

const BaseEditableProcedureDiagramSidebar = (props: Props) => {
  return (
    <BootstrapMinMediumMediaQuery>
      <BaseProcedureDiagramSidebar {...props} />
    </BootstrapMinMediumMediaQuery>
  );
};

export const EditableProcedureDiagramSidebar = makeDiagramEditable(
  BaseEditableProcedureDiagramSidebar
);
